import axios from 'axios';

const inferredApiUrl =
  typeof window !== 'undefined' && window.location?.hostname
    ? `${window.location.protocol}//${window.location.hostname}:8000`
    : 'http://localhost:8000';
const API_URL =
  process.env.REACT_APP_API_URL || inferredApiUrl;

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Adiciona o header Authorization se existir token salvo
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Atualiza token ou força logout em respostas 401
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  refreshQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Se não for 401 ou se for retry já processado, apenas rejeita
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      return Promise.reject(error);
    }

    // Evita múltiplos refresh em paralelo
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshResponse = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token });
      const newToken = refreshResponse.data?.access_token;
      const newRefresh = refreshResponse.data?.refresh_token;

      if (newToken) {
        localStorage.setItem('access_token', newToken);
        if (newRefresh) {
          localStorage.setItem('refresh_token', newRefresh);
        }
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      processQueue(new Error('Falha ao atualizar token'));
      return Promise.reject(error);
    } catch (refreshError) {
      processQueue(refreshError);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Tipo Unidade Services
export const tipoUnidadeService = {
  list: () => api.get('/api/tipo-unidades/'),
  get: (id) => api.get(`/api/tipo-unidades/${id}`),
  create: (data) => api.post('/api/tipo-unidades/', data),
  update: (id, data) => api.put(`/api/tipo-unidades/${id}`, data),
  delete: (id) => api.delete(`/api/tipo-unidades/${id}`),
};

// Unidade Services
export const unidadeService = {
  list: (params) => api.get('/api/unidades/', { params }),
  get: (id) => api.get(`/api/unidades/${id}`),
  create: (data) => api.post('/api/unidades/', data),
  update: (id, data) => api.put(`/api/unidades/${id}`, data),
  delete: (id) => api.delete(`/api/unidades/${id}`),
};

// Modalidade Services
export const modalidadeService = {
  list: () => api.get('/api/modalidades/'),
  get: (id) => api.get(`/api/modalidades/${id}`),
  create: (data) => api.post('/api/modalidades/', data),
  update: (id, data) => api.put(`/api/modalidades/${id}`, data),
  delete: (id) => api.delete(`/api/modalidades/${id}`),
};

// Serie Services
export const serieService = {
  list: (params) => api.get('/api/series/', { params }),
  get: (id) => api.get(`/api/series/${id}`),
  create: (data) => api.post('/api/series/', data),
  update: (id, data) => api.put(`/api/series/${id}`, data),
  delete: (id) => api.delete(`/api/series/${id}`),
};

// Tipo Contrato Services
export const tipoContratoService = {
  list: () => api.get('/api/tipo-contratos/'),
  get: (id) => api.get(`/api/tipo-contratos/${id}`),
  create: (data) => api.post('/api/tipo-contratos/', data),
  update: (id, data) => api.put(`/api/tipo-contratos/${id}`, data),
  delete: (id) => api.delete(`/api/tipo-contratos/${id}`),
};

// Equipe Services
export const equipeService = {
  list: (params) => api.get('/api/equipes/', { params }),
  get: (id) => api.get(`/api/equipes/${id}`),
  create: (data) => api.post('/api/equipes/', data),
  update: (id, data) => api.put(`/api/equipes/${id}`, data),
  delete: (id) => api.delete(`/api/equipes/${id}`),
  vincularUsuario: (id, data) => api.post(`/api/equipes/${id}/vincular-usuario`, data),
  desvincularUsuario: (id) => api.post(`/api/equipes/${id}/desvincular-usuario`),
};

// Turma Services
// Evento Services (modulo de Eventos, Fase A -- Evento + Sessao apenas)
export const eventoService = {
  list: (params) => api.get('/api/eventos', { params }),
  get: (id) => api.get(`/api/eventos/${id}`),
  create: (data) => api.post('/api/eventos', data),
  update: (id, data) => api.put(`/api/eventos/${id}`, data),
  cancelar: (id) => api.put(`/api/eventos/${id}/cancelar`),
  delete: (id) => api.delete(`/api/eventos/${id}`),
  listSessoes: (eventoId, params) => api.get(`/api/eventos/${eventoId}/sessoes`, { params }),
  createSessao: (eventoId, data) => api.post(`/api/eventos/${eventoId}/sessoes`, data),
  updateSessao: (eventoId, sessaoId, data) => api.put(`/api/eventos/${eventoId}/sessoes/${sessaoId}`, data),
  cancelarSessao: (eventoId, sessaoId) => api.put(`/api/eventos/${eventoId}/sessoes/${sessaoId}/cancelar`),
  deleteSessao: (eventoId, sessaoId) => api.delete(`/api/eventos/${eventoId}/sessoes/${sessaoId}`),
  // Turmas elegiveis da Sessao (Fase B.1 -- so configuracao, sem alunos ainda)
  listTurmasSessao: (eventoId, sessaoId) => api.get(`/api/eventos/${eventoId}/sessoes/${sessaoId}/turmas`),
  vincularTurmaSessao: (eventoId, sessaoId, idTurma) =>
    api.post(`/api/eventos/${eventoId}/sessoes/${sessaoId}/turmas`, { id_turma: idTurma }),
  desvincularTurmaSessao: (eventoId, sessaoId, vinculoId) =>
    api.delete(`/api/eventos/${eventoId}/sessoes/${sessaoId}/turmas/${vinculoId}`),
  // Alunos elegiveis da Sessao (Fase B.2 -- somente leitura/diagnostico)
  listAlunosElegiveisSessao: (eventoId, sessaoId) =>
    api.get(`/api/eventos/${eventoId}/sessoes/${sessaoId}/alunos-elegiveis`),
  // Capacidade / abertura de inscricoes da Sessao (Fase B.3b). NULL =
  // ilimitada, 0 = zero vagas literal -- nunca interpretado como ilimitado.
  alterarCapacidadeSessao: (eventoId, sessaoId, capacidadeTotal) =>
    api.put(`/api/eventos/${eventoId}/sessoes/${sessaoId}/capacidade`, { capacidade_total: capacidadeTotal }),
  abrirInscricoesSessao: (eventoId, sessaoId) =>
    api.put(`/api/eventos/${eventoId}/sessoes/${sessaoId}/abrir-inscricoes`),
  fecharInscricoesSessao: (eventoId, sessaoId) =>
    api.put(`/api/eventos/${eventoId}/sessoes/${sessaoId}/fechar-inscricoes`),
};

// Inscricoes de Evento -- Administracao (Fase B.3b + B.4a) -- listagem
// geral paginada/filtrada + transicoes (aprovar/rejeitar/cancelar) sobre
// app/routes/eventos_admin.py. NAO expoe EFETIVAR (fora de escopo desta
// fase). REJEITADA/CANCELADA liberam vaga automaticamente pela contagem
// dinamica de ocupacao no backend -- o frontend nunca mantem contador
// proprio de vagas.
//
// aprovarComercial/editarCondicoesComerciais (Fase B.4a) substituem o
// antigo `aprovar()` sem condicoes -- UNICO caminho valido para uma
// inscricao chegar a APROVADA agora exige valor_bruto/desconto_reais/
// quantidade_parcelas/parcelas. `condicoes` e sempre
// { valor_bruto, desconto_reais, quantidade_parcelas, parcelas: [{numero_parcela, valor, data_vencimento}] }
// -- valores monetarios como STRING (nao float), mesma convencao do
// backend (Decimal serializado como string no JSON).
export const eventoInscricaoAdminService = {
  listar: (params) => api.get('/api/eventos/inscricoes', { params }),
  detalhar: (inscricaoId) => api.get(`/api/eventos/inscricoes/${inscricaoId}`),
  aprovarComercial: (inscricaoId, condicoes) =>
    api.put(`/api/eventos/inscricoes/${inscricaoId}/aprovar-comercial`, condicoes),
  editarCondicoesComerciais: (inscricaoId, condicoes) =>
    api.put(`/api/eventos/inscricoes/${inscricaoId}/condicoes-comerciais`, condicoes),
  rejeitar: (inscricaoId, motivoRejeicao) =>
    api.patch(`/api/eventos/inscricoes/${inscricaoId}/rejeitar`, { motivo_rejeicao: motivoRejeicao || null }),
  cancelar: (inscricaoId) => api.patch(`/api/eventos/inscricoes/${inscricaoId}/cancelar`),
  efetivar: (inscricaoId) => api.put(`/api/eventos/inscricoes/${inscricaoId}/efetivar`),
  cancelarParticipacao: (participacaoId, motivoCancelamento) =>
    api.put(`/api/eventos/participacoes/${participacaoId}/cancelar`, {
      motivo_cancelamento: motivoCancelamento || null,
    }),
  // Materializacao de cobrancas (Fase B.5c.1) -- gera FinancialCharge
  // para TODAS as parcelas pendentes da inscricao de uma vez (nao e' por
  // parcela). Sem body. Idempotente no backend (chamada repetida nunca
  // duplica, so' reporta cobrancas_ja_existentes).
  gerarCobrancas: (inscricaoId) => api.post(`/api/eventos/inscricoes/${inscricaoId}/gerar-cobrancas`),
  // Materializacao de cobrancas EM LOTE (Fase 3A.2, backend Fase 3A.1 ja
  // mergeado) -- MESMO service canonico por inscricao (gerar_cobrancas_
  // inscricao), so' orquestrado em lote no backend. UMA acao do usuario
  // = UMA chamada a este endpoint -- nunca um loop client-side sobre
  // `gerarCobrancas` acima. Payload contem SOMENTE ids (nenhum dado
  // financeiro decidido pelo cliente, mesmo principio de todo o modulo).
  gerarCobrancasLote: (eventoInscricaoIds) =>
    api.post('/api/eventos/inscricoes/gerar-cobrancas-lote', { evento_inscricao_ids: eventoInscricaoIds }),
};

// Cobrancas de Eventos -- Administracao (Fase B.5c.3a/B.5c.3b) --
// listagem read-only paginada de "Eventos > Cobrancas de Eventos"
// (GET /api/eventos/cobrancas). Raiz logica e' EventoInscricaoParcela,
// nao FinancialCharge -- ver backend/app/services/evento_cobranca_admin_service.py.
// Valores monetarios (valor) chegam como STRING (Decimal serializado),
// nunca float -- mesma convencao de eventoInscricaoAdminService acima.
export const eventoCobrancaAdminService = {
  listar: (params) => api.get('/api/eventos/cobrancas', { params }),
  // Fase 2B (frontend agrupado) -- mesma listagem, porem agregada por
  // EventoInscricao (GET /api/eventos/cobrancas/agrupado, Fase 2A
  // backend). `listar` acima permanece intacto por compatibilidade,
  // nenhum consumidor existente foi alterado.
  listarAgrupado: (params) => api.get('/api/eventos/cobrancas/agrupado', { params }),
  // Emissao/reconciliacao de boleto (Fase B.5c.3c.1/B.5c.3c.2) -- sem
  // body: financial_charge_id vem SOMENTE da URL, nunca payload
  // financeiro (provider/billing_method/customer/Attempt nunca sao
  // escolhidos pelo cliente, o backend resolve tudo). Os endpoints POST
  // continuam autoritativos e revalidam integralmente no momento da
  // chamada -- este service nunca monta nem envia estado financeiro.
  emitirBoleto: (financialChargeId) =>
    api.post(`/api/eventos/cobrancas/${financialChargeId}/emitir-boleto`),
  verificarEmissao: (financialChargeId) =>
    api.post(`/api/eventos/cobrancas/${financialChargeId}/verificar-emissao`),
  // Emissao de boleto EM LOTE (Fase 3B.2, backend Fase 3B.1 ja mergeado,
  // PR #48) -- MESMO service canonico por FinancialCharge
  // (emitir_boleto_evento), so' orquestrado em lote no backend. UMA acao
  // do usuario = UMA chamada a este endpoint -- nunca um loop client-side
  // sobre `emitirBoleto` acima. Payload contem SOMENTE ids (nenhum dado
  // financeiro decidido pelo cliente, mesmo principio de todo o modulo).
  emitirBoletosLote: (financialChargeIds) =>
    api.post('/api/eventos/cobrancas/emitir-boletos-lote', { financial_charge_ids: financialChargeIds }),
  // Cancelamento canonico (Fase cancelamento-canonico) -- mesmo
  // principio: nenhum dado financeiro decidido pelo cliente. O UNICO
  // campo aceito no body e' `motivo_cancelamento` (opcional, texto
  // livre) -- nunca provider/payment_id/attempt_id/status.
  cancelar: (financialChargeId, motivoCancelamento) =>
    api.post(`/api/eventos/cobrancas/${financialChargeId}/cancelar`, {
      motivo_cancelamento: motivoCancelamento || undefined,
    }),
  verificarCancelamento: (financialChargeId) =>
    api.post(`/api/eventos/cobrancas/${financialChargeId}/verificar-cancelamento`),
  // Consolidacao de cobrancas (Fase D.3) -- formar/dissolver um
  // FinancialChargeGroup. `financial_charge_ids` e' o UNICO campo
  // aceito pelo POST (mesmo principio de todo o modulo: nenhum dado
  // financeiro decidido pelo cliente). `chave_consolidacao` (usada
  // somente para SUGERIR visualmente quais Charges agrupar, ver
  // cobrancasEventosAcoes.js) NUNCA e' enviada aqui -- o backend
  // revalida integralmente aluno/unidade/vencimento/pagador sob lock a
  // partir dos ids informados, e pode recusar mesmo uma sugestao
  // aparentemente valida (TOCTOU esperado e seguro, mesmo principio ja
  // documentado para `acao_financeira`). D.3 implementa somente formar/
  // dissolver -- emitir/cancelar/verificar/reemitir ficam para D.4/D.5.
  formarGrupo: (financialChargeIds) =>
    api.post('/api/eventos/cobrancas/grupos', { financial_charge_ids: financialChargeIds }),
  dissolverGrupo: (groupId) => api.delete(`/api/eventos/cobrancas/grupos/${groupId}`),
  // Operacoes do DOCUMENTO consolidado (Fase D.4b) -- mesmo principio de
  // formarGrupo/dissolverGrupo acima: sem body, group_id vem SOMENTE da
  // URL, nenhum dado financeiro decidido pelo cliente. REEMITIR usa o
  // MESMO endpoint de emissao (emitirBoletoGrupo) -- auditado em
  // app/services/financial_charge_group_dispatcher_service.py
  // (_decidir_estrategia_attempt_grupo, Fase C.3d.3): quando a ultima
  // FinancialChargeAttempt do grupo e' um SUCESSO ja cancelado, o
  // dispatcher libera a criacao de uma nova Attempt automaticamente --
  // o backend decide "emitir" vs "reemitir" pela historia canonica,
  // nunca o frontend. Nenhum wrapper separado para reemitir por causa
  // disso.
  emitirBoletoGrupo: (groupId) => api.post(`/api/eventos/cobrancas/grupos/${groupId}/emitir-boleto`),
  verificarPagamentoGrupo: (groupId) => api.post(`/api/eventos/cobrancas/grupos/${groupId}/verificar-pagamento`),
  cancelarBoletoGrupo: (groupId) => api.post(`/api/eventos/cobrancas/grupos/${groupId}/cancelar-boleto`),
  verificarCancelamentoGrupo: (groupId) =>
    api.post(`/api/eventos/cobrancas/grupos/${groupId}/verificar-cancelamento`),
};

export const turmaService = {
  list: (params) => api.get('/api/turmas/', { params }),
  get: (id) => api.get(`/api/turmas/${id}`),
  create: (data) => api.post('/api/turmas/', data),
  update: (id, data) => api.put(`/api/turmas/${id}`, data),
  delete: (id) => api.delete(`/api/turmas/${id}`),
  recalcularAulas: (id, onlyMissing = true) => api.post(`/api/turmas/${id}/recalcular-aulas`, null, { params: { only_missing: onlyMissing } }),
};

// Aluno Services
export const alunoService = {
  list: (params) => api.get('/api/alunos/', { params }),
  get: (id) => api.get(`/api/alunos/${id}`),
  create: (data) => api.post('/api/alunos/', data),
  update: (id, data) => api.put(`/api/alunos/${id}`, data),
  delete: (id) => api.delete(`/api/alunos/${id}`),
};

// Usuario Services
export const usuarioService = {
  list: (params) => api.get('/api/usuarios/', { params }),
  get: (id) => api.get(`/api/usuarios/${id}`),
  create: (data) => api.post('/api/usuarios/', data),
  update: (id, data) => api.put(`/api/usuarios/${id}`, data),
  delete: (id) => api.delete(`/api/usuarios/${id}`),
  buscar: (termo, limit = 20) => api.get('/api/usuarios/buscar', { params: { termo, limit } }),
  getMeuPerfil: () => api.get('/api/usuarios/me/perfil'),
  alterarMeuEmail: (data) => api.put('/api/usuarios/me/email', data),
  alterarMinhaSenha: (data) => api.put('/api/usuarios/me/senha', data),
};

// Responsavel Services
export const responsavelService = {
  list: (params) => api.get('/api/responsaveis/', { params }),
  listByAluno: (alunoId) => api.get(`/api/responsaveis/aluno/${alunoId}`),
  get: (id) => api.get(`/api/responsaveis/${id}`),
  create: (data) => api.post('/api/responsaveis/', data),
  update: (id, data) => api.put(`/api/responsaveis/${id}`, data),
  delete: (id) => api.delete(`/api/responsaveis/${id}`),
  buscarAlunosPorCpf: (cpf) => api.get(`/api/responsaveis/buscar-alunos-por-cpf/${cpf}`),
  vincularAluno: (responsavelId, data) => api.post(`/api/responsaveis/${responsavelId}/vincular-aluno`, data),
  vincularUsuario: (responsavelId, data) => api.post(`/api/responsaveis/${responsavelId}/vincular-usuario`, data),
  desvincularAluno: (responsavelId, alunoId) => api.patch(`/api/responsaveis/${responsavelId}/desvincular-aluno/${alunoId}`),
  listarAlunos: (responsavelId) => api.get(`/api/responsaveis/${responsavelId}/alunos`),
};

// Matricula Services
export const matriculaService = {
  list: (params) => api.get('/api/matriculas/', { params }),
  listAdmin: (params) => api.get('/api/matriculas/admin-list', { params }),
  get: (id) => api.get(`/api/matriculas/${id}`),
  getDetalhes: (id) => api.get(`/api/matriculas/${id}/detalhes`),
  create: (data) => api.post('/api/matriculas/', data),
  update: (id, data) => api.put(`/api/matriculas/${id}`, data),
  ativar: (id, data) => api.post(`/api/matriculas/${id}/ativar`, data),
  cancelar: (id) => api.post(`/api/matriculas/${id}/cancelar`),
  trocarVigencia: (id, data) => api.post(`/api/matriculas/${id}/trocar-vigencia`, data),
  reprogramarVigenciaFutura: (id, vigenciaId, data) =>
    api.post(`/api/matriculas/${id}/vigencias/${vigenciaId}/reprogramar`, data),
  alterarVencimentoFuturo: (id, data) => api.post(`/api/matriculas/${id}/alterar-vencimento-futuro`, data),
  corrigirVigenciaInicial: (id, data) => api.post(`/api/matriculas/${id}/corrigir-vigencia-inicial`, data),
  delete: (id) => api.delete(`/api/matriculas/${id}`),
  listParcelas: (id) => api.get(`/api/matriculas/${id}/parcelas`),
  sincronizarStatusParcelas: (id) => api.post(`/api/matriculas/${id}/parcelas/sincronizar-status`),
  recalcularParcelas: (id) => api.post(`/api/matriculas/${id}/recalcular-parcelas`),
  deleteParcelas: (id) => api.delete(`/api/matriculas/${id}/parcelas`),
  deleteParcelasBulk: (id) => api.delete(`/api/matriculas/${id}/parcelas`),
};

export const contratoService = {
  list: (params) => api.get('/api/contratos', { params }),
  get: (id) => api.get(`/api/contratos/${id}`),
  create: (data) => api.post('/api/contratos', data),
  update: (id, data) => api.put(`/api/contratos/${id}`, data),
  delete: (id) => api.delete(`/api/contratos/${id}`),
  previewPorMatricula: (matriculaId) => api.get(`/api/contratos/gerar-por-matricula/${matriculaId}/preview`),
  gerarPorMatricula: (matriculaId, data) => api.post(`/api/contratos/gerar-por-matricula/${matriculaId}`, data),
  enviarParaAssinatura: (id) => api.post(`/api/contratos/${id}/enviar-para-assinatura`),
  sincronizarStatus: (id) => api.put(`/api/contratos/${id}/sincronizar-status`),
  cancelar: (id) => api.put(`/api/contratos/${id}/cancelar`),
};

export const parcelaService = {
  list: (params) => api.get('/api/parcelas', { params }),
  delete: (id) => api.delete(`/api/parcelas/${id}`),
  getCobrancaCompartilhamento: (id) => api.get(`/api/parcelas/${id}/cobranca-compartilhamento`),
  enviarCobrancaEmail: (id) => api.post(`/api/parcelas/${id}/cobranca-compartilhamento/enviar-email`),
};

// Aulas Dadas Services
export const aulaDadaService = {
  list: (params) => api.get('/api/aulas-dadas/', { params }),
  get: (id) => api.get(`/api/aulas-dadas/${id}`),
  create: (data) => api.post('/api/aulas-dadas/', data),
  update: (id, data) => api.put(`/api/aulas-dadas/${id}`, data),
  delete: (id) => api.delete(`/api/aulas-dadas/${id}`),
  deletePendentesTurma: (turmaId) => api.delete(`/api/aulas-dadas/turma/${turmaId}/pendentes`),
  validar: (id, data) => api.post(`/api/aulas-dadas/${id}/validar`, data),
  ajustarHorario: (id, data) => api.post(`/api/aulas-dadas/${id}/ajustar-horario`, data),
  reverter: (id) => api.post(`/api/aulas-dadas/${id}/reverter`),
  relatorioMensal: (params) => api.get('/api/aulas-dadas/relatorio/mensal', { params }),
};

// Fornecedor Services
export const fornecedorService = {
  list: (params) => api.get('/api/fornecedores/', { params }),
  get: (id) => api.get(`/api/fornecedores/${id}`),
  create: (data) => api.post('/api/fornecedores/', data),
  update: (id, data) => api.put(`/api/fornecedores/${id}`, data),
  delete: (id) => api.delete(`/api/fornecedores/${id}`),
};

// Categoria Despesa Services
export const categoriaDespesaService = {
  list: (params) => api.get('/api/categorias-despesa/', { params }),
  get: (id) => api.get(`/api/categorias-despesa/${id}`),
  create: (data) => api.post('/api/categorias-despesa/', data),
  update: (id, data) => api.put(`/api/categorias-despesa/${id}`, data),
  delete: (id) => api.delete(`/api/categorias-despesa/${id}`),
};

// Despesa Services
export const despesaService = {
  list: (params) => api.get('/api/despesas/', { params }),
  get: (id) => api.get(`/api/despesas/${id}`),
  create: (data) => api.post('/api/despesas/', data),
  update: (id, data) => api.put(`/api/despesas/${id}`, data),
  delete: (id) => api.delete(`/api/despesas/${id}`),
  darBaixa: (id, data) => api.post(`/api/despesas/${id}/dar-baixa`, data ?? {}),
  reverterBaixa: (id) => api.post(`/api/despesas/${id}/reverter-baixa`),
};

// Relatórios Services
export const relatorioService = {
  repasseGlobal: (params) => api.get('/api/relatorios/repasse-global', { params }),
  matriculasAtivas: (params) => api.get('/api/relatorios/matriculas-ativas', { params }),
  matriculasAtivasOperacional: (params) => api.get('/api/relatorios/matriculas-ativas-operacional', { params }),
  professoresSimples: (params) => api.get('/api/relatorios/professores-simples', { params }),
  alunosAtivos: (params) => api.get('/api/relatorios/alunos-ativos', { params }),
  turmasAbertas: (params) => api.get('/api/relatorios/turmas-abertas', { params }),
  getRelatoriosContabeis: (params) => api.get('/api/relatorios-contabeis', { params }),
  exportRelatoriosContabeisExcel: (params) => api.get('/api/relatorios-contabeis/export/excel', {
    params,
    responseType: 'blob',
  }),
};

// Receita Services
export const receitaService = {
  list: (params) => api.get('/api/receitas/', { params }),
  get: (id) => api.get(`/api/receitas/${id}`),
  create: (data) => api.post('/api/receitas/', data),
  darBaixa: (id, data) => api.post(`/api/receitas/${id}/dar-baixa`, data),
  reverterBaixa: (id) => api.post(`/api/receitas/${id}/reverter-baixa`),
  update: (id, data) => api.put(`/api/receitas/${id}`, data),
  delete: (id) => api.delete(`/api/receitas/${id}`),
};

// Fluxo de Caixa Services
export const fluxoCaixaService = {
  get: (params) => api.get('/api/fluxo-caixa/', { params }),
};

// Folha de Pagamento Services
export const folhaPagamentoService = {
  list: (params) => api.get('/api/folha-pagamento/', { params }),
  get: (id) => api.get(`/api/folha-pagamento/${id}`),
  detalhamento: (id) => api.get(`/api/folha-pagamento/${id}/detalhamento`),
  itens: (id) => api.get(`/api/folha-pagamento/${id}/itens`),
  retroativosPendentes: (id) => api.get(`/api/folha-pagamento/${id}/retroativos-pendentes`),
  gerar: (data) => api.post('/api/folha-pagamento/gerar', data),
  gerarAjustesRetroativos: (id, data) => api.post(`/api/folha-pagamento/${id}/gerar-ajustes-retroativos`, data),
  preview: (mesReferencia, idEquipe = null) => api.get(
    `/api/folha-pagamento/preview/${mesReferencia}`,
    idEquipe ? { params: { id_equipe: idEquipe } } : undefined
  ),
  atualizarRascunho: (id) => api.post(`/api/folha-pagamento/${id}/atualizar-rascunho`),
  fechar: (id) => api.post(`/api/folha-pagamento/${id}/fechar`),
  darBaixa: (id) => api.post(`/api/folha-pagamento/${id}/dar-baixa`),
  reverterBaixa: (id) => api.post(`/api/folha-pagamento/${id}/reverter-baixa`),
  update: (id, data) => api.put(`/api/folha-pagamento/${id}`, data),
  delete: (id) => api.delete(`/api/folha-pagamento/${id}`),
};

// Perfil Services
export const perfilService = {
  list: () => api.get('/api/perfis/'),
  get: (id) => api.get(`/api/perfis/${id}`),
  create: (data) => api.post('/api/perfis/', data),
  update: (id, data) => api.put(`/api/perfis/${id}`, data),
  delete: (id) => api.delete(`/api/perfis/${id}`),
  listMenuItens: () => api.get('/api/perfis/menu-itens'),
};
// Dashboard Services
export const dashboardService = {
  getExecutivo: (params) => api.get('/api/dashboard/executivo', { params }),
  getFinanceiro: (params) => api.get('/api/dashboard/financeiro', { params }),
  getRentabilidade: (params) => api.get('/api/dashboard/rentabilidade', { params }),
  getParcelasDashboard: (params) => api.get('/api/dashboard/parcelas', { params }),
  getReceitaPorModalidade: (params) => api.get('/api/dashboard/receita-por-modalidade', { params }),
  getLucroPorTurma: (params) => api.get('/api/dashboard/lucro-por-turma', { params }),
  getLucroPorModalidade: (params) => api.get('/api/dashboard/lucro-por-modalidade', { params }),
  getForecastParcelas: (params) => api.get('/api/dashboard/forecast-parcelas', { params }),
  getTopEscolas: (params) => api.get('/api/dashboard/top-escolas', { params }),
  getOperacional: (params) => api.get('/api/dashboard/operacional', { params }),
  getComercial: (params) => api.get('/api/dashboard/comercial', { params }),
  getRH: (params) => api.get('/api/dashboard/rh', { params }),
  getFinanceiroDetalhado: (params) => api.get('/api/dashboard/financeiro-detalhado', { params }),
  getUnidades: (params) => api.get('/api/dashboard/unidades', { params }),
};

// Portal Services
export const portalService = {
  getUnidades: (params) => api.get('/api/portal/unidades', { params }),
  getAlunos: (params) => api.get('/api/portal/alunos', { params }),
  createAluno: (payload) => api.post('/api/portal/alunos', payload),
  updateAluno: (alunoId, payload) => api.put(`/api/portal/alunos/${alunoId}`, payload),
  getMatriculas: (params) => api.get('/api/portal/matriculas', { params }),
  getTurmas: (params) => api.get('/api/portal/turmas', { params }),
  getParcelas: () => api.get('/api/portal/parcelas'),
  getFaturas: () => api.get('/api/portal/faturas'),
  createMatricula: (data) => api.post('/api/portal/matriculas', data),
  getMeuCadastroResponsavel: () => api.get('/api/responsaveis/meu-cadastro'),
  updateMeuCadastroResponsavel: (data) => api.put('/api/responsaveis/meu-cadastro', data),
  // Avaliacoes de Alunos (Fase 8 do modulo) -- router backend proprio
  // (app/routes/portal_avaliacoes.py), somente leitura, somente
  // PUBLICADA (backend nunca inclui PENDENTE/RASCUNHO no contrato).
  // getAvaliacoes() traz TODAS as avaliacoes do responsavel de uma vez
  // (todas as matriculas, todos os alunos) -- o dashboard agrupa por
  // id_matricula no frontend, nunca 1 request por matricula.
  getAvaliacoes: () => api.get('/api/portal/avaliacoes'),
  getAvaliacao: (avaliacaoId) => api.get(`/api/portal/avaliacoes/${avaliacaoId}`),
};

// Portal do Responsavel -- Inscricoes de Evento (Fase B.3b) -- router
// backend proprio (app/routes/portal_eventos.py), data scope por
// usuario->aluno (NAO RBAC "eventos" administrativo). Payload de
// solicitacao e deliberadamente minimo (so id_matricula) -- id_aluno,
// status, origem_solicitacao e qualquer campo financeiro sao SEMPRE
// resolvidos/validados no servidor, nunca aceitos daqui.
export const portalEventosService = {
  // Uma linha = uma (Sessao, Matricula) elegivel, uma unica chamada para
  // TODOS os alunos/matriculas do responsavel -- nunca uma por aluno.
  getOportunidades: () => api.get('/api/portal/eventos/oportunidades'),
  // Historico completo (qualquer status, inclusive REJEITADA/CANCELADA) --
  // nunca inclui observacao_admin (nota interna administrativa).
  getMinhasInscricoes: () => api.get('/api/portal/eventos/minhas-inscricoes'),
  // Fase E.3 -- documentos financeiros (boleto individual ou
  // consolidado) dos alunos vinculados ao responsavel. READ-ONLY --
  // nenhum outro metodo de acao (emitir/cancelar/verificar) existe
  // aqui nem deve ser adicionado; o Portal so' consome Abrir boleto/
  // Copiar linha digitavel, ambos 100% client-side (ver
  // pagamentosEventosPortal.js).
  getPagamentos: () => api.get('/api/portal/eventos/pagamentos'),
  solicitar: (sessaoId, idMatricula) =>
    api.post(`/api/portal/eventos/sessoes/${sessaoId}/inscricoes`, { id_matricula: idMatricula }),
  // So permitido enquanto a inscricao esta SOLICITADA -- APROVADA retorna
  // 409 (cancelamento passa a ser administrativo).
  cancelar: (inscricaoId) => api.put(`/api/portal/eventos/inscricoes/${inscricaoId}/cancelar`),
};
// Portal do Professor Services (modulo de Frequencia)
export const professorService = {
  // data no formato local YYYY-MM-DD (sem timezone/UTC) -- escopo (equipe)
  // e resolvido inteiramente no backend a partir da sessao autenticada.
  listarEncontros: (data) => api.get('/api/professor/encontros', { params: { data } }),
  // Materializa idempotentemente os encontros previstos pela grade da
  // equipe do professor autenticado em `data` (Fase 13). Verbo PUT (nao
  // POST): o middleware global de RBAC mapeia POST -> acao "criar" e
  // PUT/PATCH -> acao "editar" -- o PROFESSOR so tem grant de editar para
  // "presencas", entao POST seria bloqueado antes mesmo da rota.
  prepararEncontros: (data) => api.put('/api/professor/encontros/preparar', null, { params: { data } }),
  getChamada: (encontroId) => api.get(`/api/professor/encontros/${encontroId}/chamada`),
  // registros: [{ matricula_id, status, observacao }] -- nunca inclui
  // equipe_id/professor_id/turma_id/usuario_id/encontro_id/autoria/
  // data_atual; o encontro já vem do path, o resto vem do usuário
  // autenticado (backend) ou é proibido pelo schema (extra="forbid").
  salvarChamada: (encontroId, registros) =>
    api.put(`/api/professor/encontros/${encontroId}/chamada`, { registros }),
};

// Portal do Professor Services (modulo de Aulas Dadas, Fase 14B) --
// router backend proprio (app/routes/portal_professor_aulas_dadas.py),
// separado do de Frequencia. Escopo (equipe) e resolvido inteiramente no
// backend a partir da sessao autenticada -- nenhuma chamada abaixo aceita
// id_equipe/professor_id.
export const professorAulasDadasService = {
  listar: (params) => api.get('/api/professor/aulas-dadas/', { params }),
  listarTurmasDisponiveis: () => api.get('/api/professor/aulas-dadas/turmas'),
  // dados: { id_turma, data_aula, hora_inicio, hora_fim, observacoes } --
  // nunca id_equipe/status/duracao_horas (rejeitados pelo schema do
  // backend, extra="forbid").
  registrar: (dados) => api.post('/api/professor/aulas-dadas/', dados),
};

// Portal do Professor Services (modulo de Turmas, Fase 14C) -- router
// backend proprio (app/routes/portal_professor_turmas.py), somente
// leitura. Escopo (equipe) e resolvido inteiramente no backend a partir
// da sessao autenticada -- esta chamada nao aceita id_equipe/professor_id.
export const professorTurmasService = {
  listar: () => api.get('/api/professor/turmas/'),
};

// Portal do Professor Services (modulo de Pagamentos, Fase 14D) -- router
// backend proprio (app/routes/portal_professor_pagamentos.py), somente
// leitura. Escopo (equipe) e resolvido inteiramente no backend a partir
// da sessao autenticada -- nenhuma chamada abaixo aceita id_equipe. O
// recibo em PDF e montado no frontend usando exclusivamente os dados de
// obter(id) -- nunca chama /api/folha-pagamento/* (endpoints
// administrativos).
export const professorPagamentosService = {
  listar: () => api.get('/api/professor/pagamentos/'),
  obter: (id) => api.get(`/api/professor/pagamentos/${id}`),
};

// Portal do Professor Services (modulo de Avaliacoes de Alunos, Fase 6
// do modulo) -- router backend proprio
// (app/routes/portal_professor_avaliacoes.py), recurso RBAC "avaliacoes"
// (distinto de "avaliacoes_gestao", administrativo). Escopo (equipe) e
// resolvido inteiramente no backend a partir da sessao autenticada --
// nenhuma chamada abaixo aceita/precisa de id_equipe/id_professor/
// autoria/id_matricula. `submeterParaValidacao` nao recebe payload algum:
// o contrato real (PUT /alunos/{id}/submeter) envia para validacao
// administrativa exatamente o que ja esta persistido via
// `salvarRascunho` -- nunca envia respostas/comentario. Fase 12: o
// professor nao publica mais diretamente -- so envia para validacao
// (RASCUNHO -> AGUARDANDO_VALIDACAO); a publicacao em si e feita por um
// usuario autorizado via avaliacoesValidacaoService, abaixo.
export const professorAvaliacoesService = {
  listar: () => api.get('/api/professor/avaliacoes'),
  listarAlunos: (avaliacaoTurmaId) => api.get(`/api/professor/avaliacoes/turmas/${avaliacaoTurmaId}/alunos`),
  obterAvaliacao: (avaliacaoAlunoId) => api.get(`/api/professor/avaliacoes/alunos/${avaliacaoAlunoId}`),
  // payload: { comentario, respostas: [{ id_periodo_criterio, nivel }] } --
  // semantica de REPLACE COMPLETO: todo criterio sem nivel selecionado
  // deve ser OMITIDO do array (nunca enviado com nivel null), pois o
  // backend remove qualquer resposta existente que nao esteja no payload.
  salvarRascunho: (avaliacaoAlunoId, payload) =>
    api.put(`/api/professor/avaliacoes/alunos/${avaliacaoAlunoId}/rascunho`, payload),
  submeterParaValidacao: (avaliacaoAlunoId) =>
    api.put(`/api/professor/avaliacoes/alunos/${avaliacaoAlunoId}/submeter`),
};

// Validação administrativa de Avaliações de Alunos (Fase 12) -- router
// backend próprio (app/routes/avaliacoes_validacao.py), recurso RBAC
// PRÓPRIO "avaliacoes_validacao" (isolado de "avaliacoes_gestao" --
// conceder acesso aqui NUNCA abre critérios/períodos/turmas). Visível a
// qualquer perfil com pode_visualizar/pode_validar nesse recurso, nunca
// restrito a ADMIN por código.
export const avaliacoesValidacaoService = {
  listarFila: () => api.get('/api/avaliacoes/validacao'),
  obterDetalhe: (avaliacaoAlunoId) => api.get(`/api/avaliacoes/validacao/${avaliacaoAlunoId}`),
  validar: (avaliacaoAlunoId) => api.put(`/api/avaliacoes/validacao/${avaliacaoAlunoId}/validar`),
  solicitarAjuste: (avaliacaoAlunoId, motivo) =>
    api.put(`/api/avaliacoes/validacao/${avaliacaoAlunoId}/solicitar-ajuste`, { motivo }),
};

// Gestão Administrativa de Avaliações de Alunos (Fase 7 do módulo) --
// router backend próprio (app/routes/avaliacoes_admin.py), recurso RBAC
// "avaliacoes_gestao" (exclusivo de ADMIN, distinto de "avaliacoes" do
// Portal do Professor acima). turmaId em todas as chamadas de turma é
// sempre Turma.id (nunca AvaliacaoTurma.id) -- mesmo identificador
// devolvido em `id_turma` por listarTurmasPeriodo/obterAcompanhamento,
// mesma convenção usada pelos endpoints administrativos reais.
export const avaliacoesAdminService = {
  // Critérios
  listarCriterios: (params) => api.get('/api/avaliacoes/criterios', { params }),
  criarCriterio: (payload) => api.post('/api/avaliacoes/criterios', payload),
  atualizarCriterio: (id, payload) => api.put(`/api/avaliacoes/criterios/${id}`, payload),

  // Períodos
  listarPeriodos: (params) => api.get('/api/avaliacoes/periodos', { params }),
  obterPeriodo: (id) => api.get(`/api/avaliacoes/periodos/${id}`),
  criarPeriodo: (payload) => api.post('/api/avaliacoes/periodos', payload),
  atualizarPeriodo: (id, payload) => api.put(`/api/avaliacoes/periodos/${id}`, payload),
  abrirPeriodo: (id) => api.put(`/api/avaliacoes/periodos/${id}/abrir`),
  encerrarPeriodo: (id) => api.put(`/api/avaliacoes/periodos/${id}/encerrar`),

  // Turmas do período
  listarTurmasPeriodo: (periodoId) => api.get(`/api/avaliacoes/periodos/${periodoId}/turmas`),
  materializarTurma: (periodoId, turmaId) => api.put(`/api/avaliacoes/periodos/${periodoId}/turmas/${turmaId}`),
  materializarTurmas: (periodoId, turmaIds) =>
    api.put(`/api/avaliacoes/periodos/${periodoId}/turmas`, { turma_ids: turmaIds }),
  obterAcompanhamento: (periodoId) => api.get(`/api/avaliacoes/periodos/${periodoId}/acompanhamento`),
  obterAlunosTurma: (periodoId, turmaId) =>
    api.get(`/api/avaliacoes/periodos/${periodoId}/turmas/${turmaId}/alunos`),

  // Criação simplificada ("Nova avaliação", ajuste pre-producao):
  // preview de turmas candidatas por data_referencia SOLTA, sem nenhum
  // período persistido ainda -- nunca chamado com um periodoId.
  listarTurmasCandidatasParaCriacao: (dataReferencia) =>
    api.get('/api/avaliacoes/turmas-candidatas', { params: { data_referencia: dataReferencia } }),
  // payload: { nome, data_referencia, data_abertura, data_encerramento,
  // turma_ids }. Operação atômica no backend -- período (já ABERTO) +
  // snapshot de critérios + materialização das turmas selecionadas, uma
  // única chamada.
  criarAvaliacaoComTurmas: (payload) => api.post('/api/avaliacoes/criar-com-turmas', payload),
};

// Gestão Administrativa de Presenças (Fases C/D) -- router backend próprio
// (app/routes/presencas_admin.py), mesmo recurso RBAC "presencas" do
// Portal do Professor acima, mas prefixo de rota distinto
// (/api/presencas, nunca /api/professor/encontros). Escopo
// (apenas_proprios) é resolvido inteiramente no backend a partir da
// sessão autenticada -- nenhuma chamada abaixo aceita/precisa de
// equipe_id "para proteger" nada; o backend já devolve somente o que o
// usuário pode acessar.
export const presencasService = {
  listarEncontros: (params) => api.get('/api/presencas/encontros', { params }),
  obterChamada: (encontroId) => api.get(`/api/presencas/encontros/${encontroId}/chamada`),
  // payload: { turma_id, data } -- get_or_create_encontro_turma() no
  // backend é idempotente: reenviar o mesmo turma_id+data nunca duplica,
  // apenas retorna o encontro já existente.
  prepararEncontro: (payload) => api.put('/api/presencas/encontros/preparar', payload),
  // payload: { registros: [{ matricula_id, status, observacao }] } --
  // nunca inclui matricula NAO_INFORMADA (o backend rejeita esse status).
  salvarChamada: (encontroId, payload) => api.put(`/api/presencas/encontros/${encontroId}/chamada`, payload),
};

export default api;
